/* Wizard · Step 1 · Case Setup
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import DetailedClinicalExam, { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import TreatmentObjectives from "@/components/case/TreatmentObjectives";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import PatientAvatar from "@/components/patient/PatientAvatar";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import DemoFillButton from "@/components/case-wizard/DemoFillButton";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import WizardPaymentCommitment from "@/components/case/WizardPaymentCommitment";
import InvisiqueLogo from "@/components/site/Logo";
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";
import {
    SelfPlanLabInstructions, StepLabInstructions, TierInclusionRow,
} from "@/components/case/wizard/steps/StepLabInstructions";
import {
    DraftSavedChip, ChildCaseBanner, StepIndicator, WizardStepHero,
    Step1SectionHeader, Step1ProgressSpine, Step1AnsweredStack,
    Step1AnsweredStrip, Step1ActiveWrap, Step1RecapBanner, RecapChip,
    ReviewBlock, Row, SelectField, SelectedPill,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
import {
    ScanSourcePicker, RadioRailRow, CoeSupportCompliancePicker,
    PlanningModePicker, ModeCard,
} from "@/components/case/wizard/pickers";
import {
    IOScannerToggleCard, TierPicker, TierFeature, LabVisitScanFeeRow,
    PromoBlock, PricingAndOffersCard, PrescriptionPreviewCard,
    PreviewBlock, PreviewRow, CoeOrderPreviewCard, PriorityPicker,
    SlotUploader, PlanningSoftwareExportCard, CoeHandoffCallout,
} from "@/components/case/wizard/blocks";


export function StepCaseSetup({
    patients, selectedPatient = null, query, setQuery, patientId, setPatientId,
    locations = [], practiceLocationId, setPracticeLocationId,
    planningMode, setPlanningMode, lockedPatient,
    coeSupportCompliance, setCoeSupportCompliance,
    tiers, pricing, setPricing, promotions,
    priorityTiers = [], allowance = null,
    isChildCase = false, caseKind = "",
    isScanVisitResume = false,
    planningCards = null,
    wizardCopy = null,
    tierInsights = null,
    ioScannerFee = 3000,
    complianceFee = 0,
    scanSourceCards = null,
    complianceCard = null,
    scanServiceHero = null,
    // iter97.83 — Scan source picker. Splits Step 1 into "Upload my
    // own scans" (continue legacy wizard) vs "Book Invisique IO
    // Scanning Service" (modal → auto-create case in
    // `scan_visit_scheduled`). For child cases (refinement / retainer)
    // we hide the picker entirely since those flows never start from
    // a fresh scan visit.
    scanSource = "",
    setScanSource = () => {},
    scannerProfile = null,
    onOpenScanVisitModal = () => {},
    onOpenAddPatient = () => {},
    draftCaseId = "",
    ensureDraft,
}) {
    /* iter92 — small inline `copy()` helper. Same shape as in the parent so
       admin overrides can apply to any string inside Step 1. */
    const copy = (k, fallback) => wizardCopy?.values?.[k] || fallback;
    const showLocationPicker = locations.length > 1;
    /* iter97.85 — Patient gate. The doctor must pick a patient BEFORE
       any of the downstream cards (submission mode + scan source) can
       be selected — those choices are case-level metadata that only
       makes sense in the context of a real patient. Child cases bypass
       this naturally because the patient is locked in by the parent.

       iter97.86 — Replaced the previous toast.error with a visual nudge:
       the Find Patient input is scrolled into view, auto-focused, and
       briefly ringed in rose so the doctor instinctively looks UP at the
       field they need to fill. Toasts felt punitive ("you did something
       wrong"); the nudge feels helpful ("here's where to go"). */
    const patientInputRef = useRef(null);
    const [patientFieldNudging, setPatientFieldNudging] = useState(false);
    const nudgePatientField = () => {
        const el = patientInputRef.current;
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Focus after the scroll settles so the cursor lands where the
            // doctor is now looking. 320ms ≈ smooth-scroll duration.
            setTimeout(() => { try { el.focus(); } catch (_) { /* noop */ } }, 320);
        }
        setPatientFieldNudging(true);
        // Ring + pulse fade naturally after the doctor sees the highlight.
        // 1.6s lets one pulse cycle play out without dragging on.
        setTimeout(() => setPatientFieldNudging(false), 1600);
    };
    const requirePatient = (setter) => (v) => {
        if (!patientId) {
            nudgePatientField();
            return;
        }
        setter(v);
    };
    const setPlanningModeGated = requirePatient(setPlanningMode);
    const setScanSourceGated   = requirePatient(setScanSource);
    // iter98.99e1 — Same patient-gating pattern: doctor must pick a
    // patient before the CoE-Support question is answerable. Mirrors
    // the planning-mode gate above so the UX is consistent.
    const setCoeSupportComplianceGated = requirePatient(setCoeSupportCompliance);
    // iter97.88 — `patients` here is the *filtered* dropdown list (empty
    // when `query` is empty), so it can't be the source of truth for
    // the selected pill. The parent computes `selectedPatient` against
    // the full patient list and passes it in; we just fall back to the
    // local list (for cases where the selected patient is still in the
    // filtered results) or the locked patient.
    const selected = patientId
        ? (lockedPatient && lockedPatient.id === patientId
            ? lockedPatient
            : (selectedPatient || patients.find((p) => p.id === patientId)))
        : null;
    const clearSelection = () => {
        setPatientId(null);
        setQuery("");
    };

    // iter98.99ae — Keyboard navigation for the suggestion list. Arrow
    // keys move the highlight, Enter selects the highlighted patient,
    // Esc clears the query. Highlight resets to the first row whenever
    // the result list changes so the doctor never lands on a stale index.
    const [highlightIdx, setHighlightIdx] = useState(0);
    useEffect(() => { setHighlightIdx(0); }, [query, patients.length]);
    const onSearchKeyDown = (e) => {
        if (!patients.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIdx((i) => (i + 1) % patients.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIdx((i) => (i - 1 + patients.length) % patients.length);
        } else if (e.key === "Enter") {
            const p = patients[Math.min(highlightIdx, patients.length - 1)];
            if (p) { e.preventDefault(); setPatientId(p.id); }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setQuery("");
        }
    };

    return (
        <div className="space-y-5">
            {/* iter100.280 — Step1ProgressSpine removed per user
                direction. The top-of-page StepIndicator already shows
                "Step 1 of 6"; a second progress bar for the *sub-steps*
                inside Step 1 was purely visual noise. */}

            {/* iter98.99l — Step 1's bespoke hero removed; the universal
                `WizardStepHero` rendered above the step body now plays
                this role for every step (and uses Step 1's primary
                accent). The "Let's set up [Patient]'s case" patient-name
                personalization is gone, but the WizardCaseHeader above
                already shows the patient name + clinic + status, so
                the doctor isn't losing context. */}

            {/* iter97.89 — Find Patient picker. Hidden when:
                - resuming a scan visit (patient locked at booking time)
                - a patient is already selected (now shown in the unified
                  WizardCaseHeader at the top of the page — duplicating
                  the card here was the user's #1 complaint about this
                  step). The "Change" link in the header lets the doctor
                  re-open this picker by clearing the selection. */}
            {!isScanVisitResume && !selected && (
                <div>
                    {/* iter98.99af — Two-line header. Bolder display title
                        on row 1, sub-helper + "Add a new Patient" pill
                        action on row 2. Earlier the label + helper +
                        link were crammed into one row of small text
                        which doctors skipped over entirely. */}
                    {/* iter101.78 — Full one-line composition.
                        Icon · Title · Search input · Add button all
                        share a single horizontal row. Title reads
                        first (left anchor), input stretches to fill,
                        Add pin sits inside the input on the right.
                        Screen readers still get the title as an <h3>
                        for section navigation; visually it now lives
                        alongside the input for a tighter, one-glance
                        widget. Below-input helper line kept for the
                        "search or create" affordance so newcomers
                        don't miss the create shortcut. */}
                    <div className="flex items-stretch gap-3 flex-wrap sm:flex-nowrap">
                        {/* Left cluster — icon + title acts as an inline
                            section label on the same row as the input. */}
                        <div className="flex items-center gap-2.5 shrink-0">
                            <div
                                className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center transition-all ring-1 ${
                                    patientFieldNudging
                                        ? "bg-rose-100 text-rose-700 ring-rose-300"
                                        : "bg-gradient-to-br from-primary/15 via-primary/8 to-primary/5 text-primary ring-primary/25"
                                }`}
                            >
                                <UserIcon size={18} weight="duotone" />
                            </div>
                            <h3
                                className={`font-display text-[15px] font-bold leading-tight tracking-tight transition-colors whitespace-nowrap ${patientFieldNudging ? "text-rose-700" : "text-foreground"}`}
                                data-testid="case-wizard-find-patient-title"
                            >
                                Find your patient
                                <span className="text-rose-600 ml-0.5">*</span>
                            </h3>
                        </div>

                        {/* Search + Add composite — fills the remaining
                            width. Nudge ring wraps just this so the
                            title/icon don't pulse alongside. */}
                        <div
                            className={`relative flex-1 min-w-0 rounded-md transition-shadow ${
                                patientFieldNudging
                                    ? "ring-2 ring-rose-400 ring-offset-2 ring-offset-card animate-pulse"
                                    : ""
                            }`}
                            data-testid="case-wizard-patient-field"
                        >
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input
                                ref={patientInputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={onSearchKeyDown}
                                placeholder="Type a name or phone number… (↑ ↓ Enter to pick)"
                                className="h-10 pl-9 pr-40 sm:pr-44"
                                data-testid="case-wizard-patient-search"
                                autoFocus
                                role="combobox"
                                aria-expanded={query.trim().length > 0 && patients.length > 0}
                                aria-controls="case-wizard-patient-results"
                                aria-autocomplete="list"
                                aria-activedescendant={patients[highlightIdx] ? `case-wizard-patient-${patients[highlightIdx].id}` : undefined}
                            />
                            {/* Add-new patient — end-adornment inside the
                                input, still with a soft vertical divider. */}
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                                <span className="h-5 w-px bg-border" aria-hidden />
                                <button
                                    type="button"
                                    onClick={() => onOpenAddPatient(query.trim())}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-primary hover:bg-primary/10 text-[12px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    data-testid="case-wizard-add-new-patient"
                                    title={query.trim() ? `Register "${query.trim()}" as a new patient` : "Register a new patient for this case"}
                                >
                                    <Plus size={12} weight="bold" />
                                    <span className="hidden sm:inline">Add new patient</span>
                                    <span className="sm:hidden">Add</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* iter98.99ad — Suggestion list. Enhanced visibility:
                        previously a flat thin-border box that doctors
                        missed entirely as they typed. Now sits on a
                        primary-tinted background with a soft glow,
                        carries a small count chip + hint header, and
                        each suggestion row hovers with a stronger
                        primary tint. */}
                    {query.trim().length > 0 && (
                        <div
                            id="case-wizard-patient-results"
                            role="listbox"
                            className="mt-2 rounded-xl border-2 border-primary/40 bg-card shadow-lg shadow-primary/10 ring-1 ring-primary/10 overflow-hidden"
                            data-testid="case-wizard-patient-results"
                        >
                            {/* Header row — gives the dropdown an obvious "this is a list" affordance */}
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary/5 border-b border-primary/20 text-[10.5px] font-bold uppercase tracking-[0.14em]">
                                <span className="text-primary inline-flex items-center gap-1.5">
                                    <MagnifyingGlass size={11} weight="bold" />
                                    {patients.length === 0 ? "No matches — create instead?" : `${patients.length} match${patients.length === 1 ? "" : "es"}`}
                                </span>
                                <span className="text-muted-foreground normal-case tracking-normal font-medium">
                                    {patients.length > 0 ? "↑ ↓ Enter · or click" : "Enter · or click"}
                                </span>
                            </div>
                            <div className="max-h-56 overflow-y-auto">
                                {patients.length === 0 ? (
                                    /* iter101.76 — Promoted "Create '{query}'
                                       as a new patient" row. Full-width
                                       clickable button styled to match a
                                       search result so doctors can either
                                       press Enter (from the search input's
                                       Ctrl-Enter shortcut) or click here
                                       to launch AddPatientDialog with the
                                       name prefilled. Replaces the tiny
                                       "Add a new patient" text link. */
                                    <button
                                        type="button"
                                        onClick={() => onOpenAddPatient(query.trim())}
                                        className="group w-full text-left px-3 py-3 flex items-center gap-3 focus:outline-none transition-colors bg-primary/[0.06] hover:bg-primary/[0.12] ring-1 ring-inset ring-primary/25"
                                        data-testid="case-wizard-add-new-patient-suggestion"
                                    >
                                        <div className="shrink-0 w-9 h-9 rounded-xl grid place-items-center bg-primary text-primary-foreground shadow-sm group-hover:shadow-md transition-shadow">
                                            <Plus size={16} weight="bold" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13.5px] font-semibold text-foreground leading-tight">
                                                Create <span className="text-primary">&ldquo;{query.trim()}&rdquo;</span> as a new patient
                                            </div>
                                            <p className="text-[11.5px] text-muted-foreground mt-0.5">
                                                Opens the registration form with the name pre-filled.
                                            </p>
                                        </div>
                                        <span className="shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold text-primary uppercase tracking-[0.14em]">
                                            Create <ArrowRight size={11} weight="bold" />
                                        </span>
                                    </button>
                                ) : (
                                    patients.map((p, idx) => {
                                        const isActive = idx === highlightIdx;
                                        return (
                                            <button
                                                key={p.id}
                                                id={`case-wizard-patient-${p.id}`}
                                                type="button"
                                                role="option"
                                                aria-selected={isActive}
                                                onClick={() => setPatientId(p.id)}
                                                onMouseEnter={() => setHighlightIdx(idx)}
                                                className={`group w-full text-left px-3 py-2.5 flex items-center gap-2.5 focus:outline-none transition-colors ${
                                                    isActive
                                                        ? "bg-primary/[0.1] ring-1 ring-inset ring-primary/30"
                                                        : "hover:bg-primary/[0.06]"
                                                } ${idx > 0 ? "border-t border-border/50" : ""}`}
                                                data-testid={`case-wizard-patient-${p.id}`}
                                            >
                                                <div className={`shrink-0 w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold uppercase transition-colors ${
                                                    isActive
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-primary/10 group-hover:bg-primary/20 text-primary"
                                                }`}>
                                                    {(p.name || "?").trim().charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] font-semibold text-foreground leading-tight truncate">
                                                        {p.name || "Unnamed"}
                                                    </div>
                                                    <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5 truncate">
                                                        {p.phone || "—"}
                                                    </div>
                                                </div>
                                                <CaretRight size={13} weight="bold" className={`shrink-0 transition-colors ${
                                                    isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                                                }`} />
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* iter101.63 — Progressive-disclosure block for the four
                Step-1 decisions. Only ONE section renders as an active
                card at any time; the rest slide up into the compact
                <Step1AnsweredStrip /> at the top of this block.

                The io_service scan-source branch remains an early
                exit — picking IO Scans opens the booking modal and
                the wizard navigates away, so we don't need to sequence
                the remaining sections in that case.

                Child cases + scan-visit resumes keep their bespoke
                inline flows (below this block) so we short-circuit
                the progressive disclosure when either is true. */}
            {!isChildCase && !isScanVisitResume && patientId && (() => {
                const scanAnswered  = scanSource === "upload";
                const planAnswered  = !!planningMode;
                const compAnswered  = coeSupportCompliance === true || coeSupportCompliance === false;
                const speedAnswered = !!(pricing?.priority_code || "").trim();

                /* Chip descriptors — only the answered sections render
                   as chips. Value strings are kept short so the whole
                   strip fits on one row at ≥sm widths. */
                /* iter101.73 — Each chip now carries a `helper` line
                   that reads back the doctor's choice in plain English
                   so the recap feels like a confirmation instead of a
                   tag. Format:
                     [Label] — [Value]
                     (you have chosen …)
                */
                const answeredChips = [];
                if (scanAnswered) {
                    answeredChips.push({
                        key: "scan-source",
                        num: "01",
                        label: "Scan Source",
                        value: "Upload my own scans",
                        helper: "You have chosen to upload scans in-house.",
                        tone: "primary",
                        onChange: () => setScanSource(""),
                    });
                }
                if (planAnswered) {
                    answeredChips.push({
                        key: "planning-mode",
                        num: "02",
                        label: "Planning Mode",
                        value: planningMode === "coe" ? "Invisique™ Team" : "Self-planned",
                        helper: planningMode === "coe"
                            ? "You have chosen our Planning Team to author the treatment plan."
                            : "You have chosen to author the treatment plan yourself.",
                        tone: "emerald",
                        onChange: () => setPlanningMode(""),
                    });
                }
                if (compAnswered) {
                    answeredChips.push({
                        key: "compliance",
                        num: "03",
                        label: "Compliance",
                        value: coeSupportCompliance === true ? "Invisique™ manages" : "Self-managed",
                        helper: coeSupportCompliance === true
                            ? "You have opted in for our CoE-Support compliance monitoring."
                            : "You have chosen to self-manage patient compliance.",
                        tone: "rose",
                        onChange: () => setCoeSupportCompliance(null),
                    });
                }
                if (speedAnswered) {
                    const code = (pricing?.priority_code || "").toLowerCase();
                    const tatDays = pricing?.priority_tat_days;
                    const tatText = tatDays ? ` (${tatDays}-day turnaround)` : "";
                    const helperByCode = {
                        standard: `Standard turnaround${tatText} — no rush fees.`,
                        rush:     `Rush turnaround${tatText} — faster than standard, small add-on.`,
                        urgent:   `Urgent turnaround${tatText} — expedited manufacturing.`,
                        stat:     `Stat turnaround${tatText} — highest priority manufacturing.`,
                    };
                    // iter101.74 — Title-case the speed label so admin-set
                    // codes like "RUSH" render as "Rush" in the confirmation
                    // chip instead of shouting all-caps at the doctor.
                    const rawSpeedValue = pricing?.priority_label || pricing?.priority_code || "";
                    const prettySpeed = rawSpeedValue
                        .toString()
                        .toLowerCase()
                        .replace(/\b\w/g, (ch) => ch.toUpperCase());
                    answeredChips.push({
                        key: "mfg-speed",
                        num: "04",
                        label: "Manufacturing Speed",
                        value: prettySpeed,
                        helper: helperByCode[code] || `You have chosen ${prettySpeed || code} manufacturing turnaround.`,
                        tone: "amber",
                        onChange: () => setPricing((p) => ({
                            ...p,
                            priority_tier_id: null,
                            priority_code: "",
                            priority_label: "",
                            priority_fee: 0,
                            priority_tat_days: 0,
                        })),
                    });
                }

                /* Active-section resolution — first unanswered section
                   wins. Once all four are answered the block renders
                   just the strip (no active card). */
                let active = null;
                if (!scanAnswered) active = "scan_source";
                else if (!planAnswered) active = "planning_mode";
                else if (!compAnswered) active = "compliance";
                else if (!speedAnswered) active = "mfg_speed";

                return (
                    <div className="space-y-4">
                        <Step1AnsweredStack chips={answeredChips} />

                        {active === "scan_source" && (
                            <Step1ActiveWrap testId="step1-active-scan-source">
                                <Step1SectionHeader
                                    num="01"
                                    icon={Cube}
                                    title="Scan source"
                                    helper="What's the scan capture method for this patient?"
                                    accent="primary"
                                />
                                <ScanSourcePicker
                                    value={scanSource}
                                    onChange={setScanSourceGated}
                                    scannerProfile={scannerProfile}
                                    ioScannerFee={ioScannerFee}
                                    onBookScanVisit={onOpenScanVisitModal}
                                    patientSelected={!!patientId}
                                    hideHeader
                                    scanSourceCards={scanSourceCards}
                                    scanServiceHero={scanServiceHero}
                                />
                            </Step1ActiveWrap>
                        )}

                        {active === "planning_mode" && (
                            <Step1ActiveWrap testId="step1-active-planning-mode">
                                <Step1SectionHeader
                                    num="02"
                                    icon={Crown}
                                    title="Treatment Planning Mode"
                                    helper="Choose how this case gets planned — your expertise, your call."
                                    accent="emerald"
                                />
                                <PlanningModePicker
                                    mode={planningMode}
                                    setMode={setPlanningModeGated}
                                    cards={planningCards}
                                    wizardCopy={wizardCopy}
                                    hideHeader
                                />
                            </Step1ActiveWrap>
                        )}

                        {active === "compliance" && (
                            <Step1ActiveWrap testId="step1-active-compliance">
                                <CoeSupportCompliancePicker
                                    value={coeSupportCompliance}
                                    onChange={setCoeSupportComplianceGated}
                                    patientSelected={!!patientId}
                                    fee={complianceFee}
                                    cardSettings={complianceCard}
                                    hideCard
                                />
                            </Step1ActiveWrap>
                        )}

                        {active === "mfg_speed" && (
                            <Step1ActiveWrap testId="step1-active-mfg-speed">
                                <div data-testid="case-wizard-coe-step1-preferences">
                                    <Step1SectionHeader
                                        num="04"
                                        icon={Lightning}
                                        title="Manufacturing speed"
                                        helper="How quickly should we manufacture and ship? Faster = treatment starts sooner."
                                        accent="amber"
                                    />
                                    <PriorityPicker
                                        priorityTiers={priorityTiers}
                                        pricing={pricing}
                                        setPricing={setPricing}
                                        hideHeader
                                    />
                                </div>
                            </Step1ActiveWrap>
                        )}
                    </div>
                );
            })()}

            {/* iter101.63 — Legacy inline branches preserved for
                the scan-visit-resume flow (patient already committed
                on booking → planning mode the ONLY question).
                iter101.70 — The `io_service` standalone block below
                was removed. Picking IO Scans keeps the scan-source
                section active inside the progressive-disclosure
                block above (Step1ActiveWrap), which already renders
                the IO service hero via `<ScanSourcePicker />`. The
                extra block was rendering a duplicate SCAN SOURCE
                card immediately below. */}
            {isScanVisitResume && (
                <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/30 via-white to-emerald-50/10 p-5 shadow-sm">
                    <Step1SectionHeader
                        num="02"
                        icon={Crown}
                        title="Treatment Planning Mode"
                        helper="Choose how this case gets planned — your expertise, your call."
                        accent="emerald"
                    />
                    <PlanningModePicker
                        mode={planningMode}
                        setMode={setPlanningModeGated}
                        cards={planningCards}
                        wizardCopy={wizardCopy}
                        locked={planningMode === "coe" || planningMode === "doctor"}
                        lockedReason="You committed to this mode when booking the scan visit. The choice carries through every remaining wizard step."
                        hideHeader
                    />
                </div>
            )}
            {isScanVisitResume && !!planningMode && (
                <CoeSupportCompliancePicker
                    value={coeSupportCompliance}
                    onChange={setCoeSupportComplianceGated}
                    patientSelected={!!patientId}
                    fee={complianceFee}
                    cardSettings={complianceCard}
                />
            )}

            {/* iter98.99b — Scan source picker MOVED above (see top of
                step). Original location removed. */}

            {showLocationPicker && (
                <div>
                    <Label className="text-xs font-semibold">Practice location</Label>
                    <select
                        value={practiceLocationId || ""}
                        onChange={(e) => setPracticeLocationId(e.target.value)}
                        className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="case-wizard-practice-location"
                    >
                        {locations.map((l) => (
                            <option key={l.id} value={l.id}>
                                {l.name}{l.is_primary ? " · Primary" : ""}{l.city ? ` — ${l.city}` : ""}
                            </option>
                        ))}
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-1">
                        Which clinic is this case being treated at? Aligners will be shipped to the selected location.
                    </p>
                </div>
            )}

            {/* iter97.83 — Tier/Priority/Pricing only appear once the
                doctor has picked "Upload my own scans" (or the case is
                a child case — those skip the picker). For
                `io_service` we surface the dedicated CTA instead;
                tier+pricing decisions then happen AFTER the scans
                land (case status: scan_visit_completed → wizard
                resume).
                iter98.99c — Restricted to child-cases-only. All inner
                content was already gated by `isChildCase && ...`, so
                for non-child cases this wrapper rendered as an empty
                `border-t` line — the source of the double-divider the
                user spotted under Submission mode. */}
            {isChildCase && (
            <div className="border-t border-border pt-4 space-y-3">
                {/* iter92 — child cases (refinements / retainers) ALWAYS inherit
                    the parent's tier. The doctor never picks tier here. We
                    show one of 3 status banners and hide the TierPicker entirely. */}
                {isChildCase && allowance?.covered && (
                    <div
                        className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"
                        data-testid="case-wizard-tier-bypass"
                    >
                        <div className="flex items-start gap-3">
                            <CheckCircle size={20} className="text-emerald-600 shrink-0 mt-0.5" weight="fill" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-emerald-900">
                                    Included in {allowance.tier_name || "your treatment plan"}
                                </div>
                                <p className="text-xs text-emerald-800 mt-0.5">
                                    {caseKind === "retainer"
                                        ? `This retainer is covered by the parent case's tier — no extra lab charge. ${allowance.remaining - 1} retainer(s) remaining after this one.`
                                        : `Refinement ${allowance.used + 1} of ${allowance.included} — covered by the parent case's tier. No extra lab charge${allowance.aligners_per_refinement ? ` for up to ${allowance.aligners_per_refinement} aligners.` : "."}`}
                                </p>
                                <div className="mt-2 text-[11px] text-emerald-700 font-mono">
                                    {allowance.used + 1}/{allowance.included} used &nbsp;·&nbsp;
                                    Lab charge: ₹0
                                </div>
                                {caseKind === "refinement" && allowance.aligners_per_refinement > 0 && allowance.overage_rate > 0 && (
                                    <div className="mt-2 text-[11px] text-emerald-800/80 leading-relaxed">
                                        Soft cap: if this refinement needs more than <strong>{allowance.aligners_per_refinement}</strong> aligners,
                                        the extras bill at <strong>{fmtINR(allowance.overage_rate)}</strong>/aligner.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {isChildCase && allowance && !allowance.covered && allowance.tier_id && (
                    <div className="space-y-4" data-testid="case-wizard-tier-overage-wrap">
                        <div
                            className="bg-amber-50 border border-amber-200 rounded-xl p-4"
                            data-testid="case-wizard-tier-overage"
                        >
                            <div className="flex items-start gap-3">
                                <Warning size={20} className="text-amber-600 shrink-0 mt-0.5" weight="fill" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-amber-900">
                                        You've reached your {caseKind === "retainer" ? "retainer" : "refinement"} limit
                                    </div>
                                    <p className="text-xs text-amber-800 mt-0.5">
                                        {allowance.tier_name
                                            ? `${allowance.tier_name} included ${allowance.included} ${caseKind === "retainer" ? "retainer(s)" : "refinement(s)"} — all used.`
                                            : ""}
                                        {" "}To request another {caseKind === "retainer" ? "retainer" : "refinement"}, please pick a treatment tier below — the chosen tier's lab charge applies and its bundled allowance covers any further {caseKind === "retainer" ? "retainer" : "refinement"} on this case.
                                    </p>
                                    <div className="mt-2 text-[11px] text-amber-700 font-mono">
                                        {allowance.used}/{allowance.included} used &nbsp;·&nbsp;
                                        Inherited from {allowance.tier_name || "parent case"}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* iter92 — overage tier picker. Doctor selects a tier; the
                            child case becomes a standalone tier purchase (parent
                            allowance is exhausted, doesn't apply). We mark the
                            pricing snapshot so submit + audit can tell apart
                            "covered by parent bundle" vs "fresh tier on overage". */}
                        <div data-testid="case-wizard-overage-tier-picker">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Pick a tier for this {caseKind === "retainer" ? "retainer" : "refinement"}</div>
                            <h3 className="font-display text-base font-bold mt-0.5">Choose a treatment tier that best fits this case.</h3>
                            <TierPicker
                                tiers={tiers}
                                pricing={pricing}
                                setPricing={(next) => {
                                    // Mark this as an overage tier purchase so the
                                    // submit logic skips parent-allowance draining.
                                    const merged = typeof next === "function" ? next(pricing) : next;
                                    setPricing({ ...merged, overage_tier_purchase: true });
                                }}
                                tierInsights={tierInsights}
                                planningMode={planningMode}
                            />
                        </div>
                    </div>
                )}

                {isChildCase && allowance && !allowance.tier_id && (
                    <div
                        className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                        data-testid="case-wizard-tier-no-parent"
                    >
                        <div className="flex items-start gap-3">
                            <Warning size={20} className="text-slate-500 shrink-0 mt-0.5" weight="fill" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-slate-900">
                                    Parent case has no tier on record
                                </div>
                                <p className="text-xs text-slate-700 mt-0.5">
                                    This {caseKind || "child case"} will be billed at standard add-on rates.
                                    Your admin will reconcile pricing when the invoice is issued.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* iter97.89 — The "Tier & commercials come at the end"
                    info card was scaffolding from when CoE used to pick
                    tier inline on Step 1 and self-plan deferred it. Now
                    that BOTH modes defer to Step 6, the step indicator
                    "Choose Tier" already signals this clearly — the
                    extra card was just noise. Removed entirely. */}
            </div>
            )}

            {/* iter98.99 — Manufacturing Speed picker (Section 03 in
                the doctor's Step 1).
                iter98.99o — Self-Plan also gets the picker on Step 1
                now that its old Step 6 "Choose Tier" is gone (tier is
                auto-assigned from aligner count → no separate step).
                Both planning modes share this picker, matching the
                wizard's 6-step structure.
                iter98.99k — Wrapped in its own card with the amber
                accent ("speed" reads warm/urgent in color theory).
                iter100.113q — Progressive disclosure: also stays
                hidden until the doctor has answered the Compliance
                Monitoring & Support Yes/No question.
                iter101.63 — Restricted to scan-visit-resume flow.
                Non-resume + non-child cases now render this picker
                inside the new progressive-disclosure block above. */}
            {!isChildCase && isScanVisitResume && (coeSupportCompliance === true || coeSupportCompliance === false) && (
                <div
                    className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/20 p-5 shadow-sm"
                    data-testid="case-wizard-coe-step1-preferences"
                >
                    <Step1SectionHeader
                        num="04"
                        icon={Lightning}
                        title="Manufacturing speed"
                        helper="How quickly should we manufacture and ship? Faster = treatment starts sooner."
                        accent="amber"
                    />
                    <PriorityPicker
                        priorityTiers={priorityTiers}
                        pricing={pricing}
                        setPricing={setPricing}
                        hideHeader
                    />
                </div>
            )}
        </div>
    );
}
